import React, { useContext, useCallback, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import Global from '../../../../store/global-selectors';
import { FormattedMessage } from 'react-intl';
import { ProductDetailsContext } from '../../context';
import { ProductVariant } from './product-variant';
import { messages } from './messages';
import classnames from 'clsx';
import ProductAutoComplete from './../../../../components/product-auto-complete/index';
import { Button } from 'ui-components/components/button';
import { LoadingIndicator } from 'ui-components/components/loading-indicator';
import AsModalWithContext from '../../../../components/as-modal/with-context';
import { AddVariantWarningModal } from './add-variant-warning-modal';
import { addProductVariant } from '../../../../store/products/products.api';
import { bulkLoadProductData } from '../../../../store/products/products.actions';
import { ProductDetailsService } from '../../../../services/product-details.service';
import style from '../../style.module.less';
import generalStyle from './style.module.less';

export const ProductVariants = () => {
  const { productId, productVariantIds } = useContext(ProductDetailsContext);
  const dispatch = useDispatch();
  const [selectedProduct, setSelectedProduct] = useState();
  const [freeTextSku, setFreeTextSku] = useState('');
  const [freeTextName, setFreeTextName] = useState('');
  const [newlyAddedVariantProducts, setNewlyAddedVariantProducts] = useState([]);
  const productsFromEntities = useSelector((state) =>
    Global.selectEntitiesProductsByIds(state, {
      productIds: productVariantIds,
    }),
  );
  const products = [...productsFromEntities, ...newlyAddedVariantProducts];
  const onProductSelected = useCallback(
    (product) => {
      setSelectedProduct(product);
    },
    [setSelectedProduct],
  );
  const onInputCleared = useCallback(() => {
    setSelectedProduct(undefined);
    setFreeTextSku('');
    setFreeTextName('');
  }, [setSelectedProduct, setFreeTextSku, setFreeTextName]);
  const [isLoading, setIsLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const hasParent = !!selectedProduct?.parentProductId;
  const addNewVariant = useCallback(() => {
    const sku = selectedProduct?.sku || freeTextSku;
    const name = selectedProduct?.name || freeTextName;
    const addVariantPromise = addProductVariant(productId, selectedProduct?.productId, sku, name);
    addVariantPromise.promise
      .then((response) => {
        const [product] = response?.data || [];
        dispatch(bulkLoadProductData([product?.productId, productId]));
        setNewlyAddedVariantProducts([...newlyAddedVariantProducts, product]);
        setIsLoading(false);
        onInputCleared();
      })
      .catch(() => {
        ProductDetailsService.showGeneralErrorNotification();
      });

    return () => addVariantPromise?.cancel();
  }, [
    productId,
    selectedProduct,
    freeTextSku,
    freeTextName,
    newlyAddedVariantProducts,
    setNewlyAddedVariantProducts,
    dispatch,
    onInputCleared,
    setIsLoading,
  ]);
  const openModal = useCallback(() => {
    setIsModalOpen(true);
  }, [setIsModalOpen]);
  const closeModal = useCallback(() => {
    setIsModalOpen(false);
  }, [setIsModalOpen]);
  const onConfirm = useCallback(() => {
    setIsModalOpen(false);
    setIsLoading(true);
    addNewVariant();
  }, [setIsLoading, setIsModalOpen, addNewVariant]);
  const onAddedVariantRemoved = useCallback(
    (prodId) => {
      setNewlyAddedVariantProducts(newlyAddedVariantProducts.filter((product) => product.productId !== prodId));
    },
    [setNewlyAddedVariantProducts, newlyAddedVariantProducts],
  );
  const selectedProductTextSku = selectedProduct?.sku;
  const selectedProductTextName = selectedProduct?.name;
  const freeTextEntryIsSufficient = freeTextSku.length > 0 && freeTextName.length > 0;
  const isAddButtonDisabled = !selectedProduct && !freeTextEntryIsSufficient;
  return (
    <>
      <div className={generalStyle.variants}>
        <div className={classnames(style['shipping-defaults-header-text'], generalStyle['variants-title'])}>
          <FormattedMessage {...messages.productVariantsTitle} />
        </div>
        <div className={generalStyle['variants-wrapper']}>
          <div className={generalStyle['variants-header']}>
            <div>
              <FormattedMessage {...messages.productVariantsSku} />
            </div>
            <div>
              <FormattedMessage {...messages.productVariantsName} />
            </div>
            <div>
              <FormattedMessage {...messages.productVariantsActive} />
            </div>
            <div>
              <FormattedMessage {...messages.productVariantsActions} />
            </div>
          </div>
          {products.map((product) => {
            return (
              <ProductVariant
                key={product.productId}
                isActive={product.active}
                name={product.name}
                onAddedVariantRemoved={onAddedVariantRemoved}
                parentProductId={productId}
                productId={product.productId}
                sku={product.sku}
              />
            );
          })}
          <div
            className={classnames(generalStyle['variants-footer'], {
              [generalStyle['variant-loading']]: isLoading,
            })}
          >
            {isLoading && <LoadingIndicator className={generalStyle['variant-loading-indicator']} />}
            {!isLoading && (
              <>
                <div>
                  <ProductAutoComplete
                    dropdownClassName={generalStyle['auto-complete-dropdown']}
                    onInputCleared={onInputCleared}
                    onSelectionMade={onProductSelected}
                    searchTerm={freeTextSku}
                    selectedProductText={selectedProductTextSku}
                    setSearchTerm={setFreeTextSku}
                  />
                </div>
                <div>
                  <ProductAutoComplete
                    dropdownClassName={generalStyle['auto-complete-dropdown']}
                    onInputCleared={onInputCleared}
                    onSelectionMade={onProductSelected}
                    searchTerm={freeTextName}
                    selectedProductText={selectedProductTextName}
                    setSearchTerm={setFreeTextName}
                  />
                </div>
                <div className={generalStyle['add-variant']}>
                  <Button disabled={isAddButtonDisabled} onClick={hasParent ? openModal : addNewVariant} theme='white'>
                    <FormattedMessage {...messages.productAddVariant} />
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      {isModalOpen && (
        <AsModalWithContext Context={ProductDetailsContext}>
          <AddVariantWarningModal onCloseModal={closeModal} onConfirm={onConfirm} />
        </AsModalWithContext>
      )}
    </>
  );
};
